import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SEO from '../components/SEO';

const CookiePolicy = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      <SEO 
        title="Cookie Policy - LXDIA | How We Use Cookies"
        description="LXDIA's Cookie Policy: Understand how we use cookies and similar technologies to enhance your browsing experience on our website."
        keywords="LXDIA cookie policy, cookies, website tracking, user preferences"
        canonical="https://lxdia.com/cookies"
      />
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Link to="/" className="inline-flex items-center text-gray-400 hover:text-white mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>

        <h1 className="text-4xl md:text-5xl font-bold mb-4">Cookie Policy</h1>
        <p className="text-gray-400 mb-12">Last updated: November 8, 2025</p>

        <div className="space-y-8 text-gray-300">
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">1. What Are Cookies?</h2>
            <p className="mb-4">
              Cookies are small text files that are placed on your device when you visit our website. 
              They help us provide you with a better experience by remembering your preferences and 
              understanding how you use our site.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">2. Types of Cookies We Use</h2>
            
            <h3 className="text-xl font-semibold text-white mb-3">Essential Cookies</h3>
            <p className="mb-4">
              These cookies are necessary for the website to function properly. They enable basic 
              functions like page navigation, security, and access to secure areas of the website. 
              The website cannot function properly without these cookies.
            </p>

            <h3 className="text-xl font-semibold text-white mb-3">Analytics Cookies</h3>
            <p className="mb-4">
              We use analytics cookies to understand how visitors interact with our website. This helps 
              us improve our services and user experience. These cookies collect information such as:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
              <li>Number of visitors</li>
              <li>Pages visited</li>
              <li>Time spent on pages</li>
              <li>Traffic sources</li>
              <li>User journey through the site</li>
            </ul>

            <h3 className="text-xl font-semibold text-white mb-3">Functional Cookies</h3>
            <p className="mb-4">
              These cookies allow the website to remember choices you make (such as your language 
              preference or region) and provide enhanced, more personalized features.
            </p>

            <h3 className="text-xl font-semibold text-white mb-3">Marketing Cookies</h3>
            <p className="mb-4">
              These cookies track your online activity to help advertisers deliver more relevant 
              advertising or to limit how many times you see an ad. These cookies can share that 
              information with other organizations or advertisers.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">3. Third-Party Cookies</h2>
            <p className="mb-4">
              We may use third-party service providers who set cookies on our website for various 
              purposes, including:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Google Analytics:</strong> To analyze website traffic and usage patterns</li>
              <li><strong>Social Media Platforms:</strong> To enable social sharing features</li>
              <li><strong>Marketing Tools:</strong> To deliver targeted advertising and measure campaign effectiveness</li>
              <li><strong>Email Services:</strong> To track email campaign performance</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">4. How We Use Cookies</h2>
            <p className="mb-4">We use cookies to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Keep you signed in to your account</li>
              <li>Remember your preferences and settings</li>
              <li>Understand how you use our website</li>
              <li>Improve website performance and user experience</li>
              <li>Personalize content and recommendations</li>
              <li>Deliver relevant advertising</li>
              <li>Measure the effectiveness of our marketing campaigns</li>
              <li>Prevent fraud and enhance security</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">5. Cookie Duration</h2>
            <h3 className="text-xl font-semibold text-white mb-3">Session Cookies</h3>
            <p className="mb-4">
              These are temporary cookies that expire when you close your browser. They help us 
              maintain your session as you navigate through our website.
            </p>

            <h3 className="text-xl font-semibold text-white mb-3">Persistent Cookies</h3>
            <p className="mb-4">
              These cookies remain on your device for a set period or until you delete them. They 
              help us remember your preferences for future visits and provide a more personalized 
              experience.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">6. Managing Cookies</h2>
            <p className="mb-4">
              You have the right to decide whether to accept or reject cookies. You can manage your 
              cookie preferences through:
            </p>

            <h3 className="text-xl font-semibold text-white mb-3">Browser Settings</h3>
            <p className="mb-4">
              Most web browsers allow you to control cookies through their settings. You can set your 
              browser to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
              <li>Block all cookies</li>
              <li>Block third-party cookies</li>
              <li>Delete cookies when you close your browser</li>
              <li>Alert you when cookies are being sent</li>
            </ul>

            <h3 className="text-xl font-semibold text-white mb-3">Opt-Out Tools</h3>
            <p className="mb-4">You can opt-out of specific cookie types:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Google Analytics: <a href="https://tools.google.com/dlpage/gaoptout" className="text-blue-400 hover:underline">Google Analytics Opt-out Browser Add-on</a></li>
              <li>Advertising cookies: Visit <a href="http://www.youronlinechoices.com" className="text-blue-400 hover:underline">Your Online Choices</a></li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">7. Impact of Disabling Cookies</h2>
            <p className="mb-4">
              If you choose to disable cookies, some features of our website may not function properly. 
              You may not be able to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Stay logged in to your account</li>
              <li>Save your preferences</li>
              <li>Access certain personalized features</li>
              <li>View embedded content from third-party sites</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">8. Do Not Track Signals</h2>
            <p className="mb-4">
              Some browsers include a "Do Not Track" (DNT) feature that signals to websites you visit 
              that you do not want to be tracked. Currently, there is no industry standard for how to 
              respond to DNT signals. We do not currently respond to DNT signals.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">9. Updates to This Cookie Policy</h2>
            <p className="mb-4">
              We may update this Cookie Policy from time to time to reflect changes in our practices 
              or for legal, operational, or regulatory reasons. We encourage you to review this policy 
              periodically.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">10. More Information</h2>
            <p className="mb-4">
              For more information about how we handle your personal data, please review our Privacy 
              Policy. If you have questions about our use of cookies, please contact us at:
            </p>
            <p className="mb-2">Email: collab@lxdia.com</p>
            <p>Location: Mumbai, India</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;
